import type {
	AxiosError,
	AxiosInstance,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import { silentlyCheckKeycloakSessionAndRedirectToAppropriatePage } from '../auth/silentSessionExpiredHandler';
import { showGlobalAlert } from '../contexts/AlertContext';
import { ACCESS_DENIED_REDIRECT_ERROR_MESSAGE } from '../error/accessErrorNavigation';
import { ROUTE_PATHS } from '../routes/routePaths';
import { API_REQUEST_LIMIT, apiMaxRetryCount, apiRetryBaseDelayMs, apiURL, errorLogsAPIBaseurl, fileExportURL, htmlToExcelURL, loginWithKeycloak, v2ApiURL } from '../util/constants';
import { isRememberMeEnabled } from '../util/decodeJWT';
import { getGlobalNavigate } from '../util/globalNavigate';
import { logger } from '../util/logger';
import { getNotFoundUrlFromCurrentPath, rememberNotFoundAttemptedUrl } from '../util/notFoundUrl';
import { redirectToKeyCloakLoginWithError } from '../util/redirectToKeyCloakLoginWithError';
import { classifyAxiosError, describeNetworkError } from './errorHandling/errorClassification.util';
import type { ErrorLogPayload } from './errorHandling/errorHandling.types';
import { delay, getRetryDelayMs } from './errorHandling/retry.util';
import authService from './authService';
import {
	attachErrorLogPayload,
	buildApiErrorLogDetails,
	buildBaseErrorLogPayload,
	captureBinaryErrorResponseBody,
	getOrCreateErrorLogPayload,
	logError,
} from './errorLogger';
import { refreshAccessTokenSafely } from './tokenRefresh';

/**
 * Show the global “no internet” message only when the browser is offline or the failure
 * matches Chrome’s net::ERR_INTERNET_DISCONNECTED (DevTools). Other no-response cases
 * (server down, CORS, DNS, etc.) keep the raw Axios error without this copy.
 */
function isNoInternetOrDisconnectedError(error: AxiosError): boolean {
	if (typeof navigator !== 'undefined' && !navigator.onLine) {
		return true;
	}
	const parts: Array<string> = [error.message, String(error.code ?? '')];
	const cause = (error as { cause?: unknown }).cause;
	if (cause instanceof Error) {
		parts.push(cause.message, String((cause as { code?: string }).code ?? ''));
	}
	const blob = parts.join(' ').toUpperCase();
	return blob.includes('ERR_INTERNET_DISCONNECTED') || blob.includes('INTERNET_DISCONNECTED');
}

async function handleUnauthorizedForSessionExpiry(useKeycloak: boolean): Promise<void> {
	if (useKeycloak) {
		if (isRememberMeEnabled()) {
			await silentlyCheckKeycloakSessionAndRedirectToAppropriatePage();
			return;
		}
		await redirectToKeyCloakLoginWithError('SESSION_EXPIRED');
		return;
	}

	authService.customLogout();
	globalThis.location.href = `${ROUTE_PATHS.LOGIN}?error=sessionExpired`;
}

// ✅ Global Request Queue
class RequestQueue {
	private readonly queue: Array<() => void> = [];
	private activeRequests = 0;
	private maxConcurrent: number;

	public constructor(maxConcurrent: number = 25) {
		this.maxConcurrent = maxConcurrent;
	}

	public async enqueue<T>(requestFn: () => Promise<T>): Promise<T> {
		while (this.activeRequests >= this.maxConcurrent) {
			// eslint-disable-next-line no-await-in-loop
			await new Promise<void>((resolve) => {
				this.queue.push(resolve);
			});
		}

		this.activeRequests++;

		try {
			return await requestFn();
		} finally {
			this.activeRequests--;
			const next = this.queue.shift();
			if (next) next();
		}
	}

	public setMaxConcurrent(limit: number): void {
		this.maxConcurrent = limit;
	}

	public getActiveCount(): number {
		return this.activeRequests;
	}

	public getQueueLength(): number {
		return this.queue.length;
	}
}

// Create global queue instance (adjust maxConcurrent as needed)
const requestQueue = new RequestQueue(API_REQUEST_LIMIT);

const axiosInstance: AxiosInstance = axios.create({
	baseURL: apiURL,
	withCredentials: true,
});

const failedQueue: Array<{
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null): void => {
	failedQueue.forEach(({ resolve, reject }) => {
		if (error) {
			reject(error);
		} else {
			resolve(token!);
		}
	});
	failedQueue.length = 0;
};

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
	const currentPath = `${globalThis.location.origin}${globalThis.location.pathname}`;
	if (config.url?.includes('start-now-proxy/apiv3')) {
		config.baseURL = process.env.REACT_APP_PB_API_BASE_URL;
	}
	if (config.url?.includes('/x/api/agent/') || config.url?.includes('/api/agent/')) {
		config.baseURL = process.env.REACT_APP_PB_API_BASE_URL;
	}
	if (globalThis.window !== undefined) {
		config.headers['X-Current-Path'] = currentPath;
	}
	config.headers['X-Request-ID'] = crypto.randomUUID();
	config.headers['X-Request-From'] = 'V2';
	return config;
};

/** Navigation-only sentinel errors ("Redirecting to …") carry the real failure's
 * logged payload, so downstream handlers reuse its correlationId. */
function createRedirectSentinel(message: string, payload: ErrorLogPayload | undefined): Error {
	const sentinel = new Error(message);
	if (payload) {
		attachErrorLogPayload(sentinel, payload);
	}
	return sentinel;
}

const createResponseInterceptor = (instance: AxiosInstance) => {
	return async (error: AxiosError): Promise<AxiosResponse> => {
		const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
		const useKeycloak = loginWithKeycloak === 'true';

		// Skip logging for error-log API requests to avoid loops/noise
		const isErrorLogRequest =
			originalRequest?.baseURL === errorLogsAPIBaseurl ||
			(typeof originalRequest?.url === 'string' && originalRequest.url.includes('/logs'));

		// The error-logs client (/logs, /errors/email, /public/errors/email) is
		// fire-and-forget telemetry/support tooling, not a real application API call —
		// a 401 from it must never refresh the token, retry, or redirect the user to
		// login (that would abandon whatever page/dialog they're actually on just
		// because a background log or "email this error" send failed). Reject as-is
		// and let the caller's own handling take over: logError's queue/retry for
		// /logs, emailErrorLog's explicit public-endpoint fallback for the email
		// variants.
		if (isErrorLogRequest) {
			throw error instanceof Error ? error : new Error(String(error));
		}

		// Blob/ArrayBuffer error bodies (responseType 'blob' downloads, PDF range
		// requests) are read as text up front so the log shows the server's actual
		// message instead of "{}".
		await captureBinaryErrorResponseBody(error);

		const shouldLog = !isErrorLogRequest;
		// Returns the payload it logged (undefined when it deliberately logs nothing) and
		// attaches it to the error(s) it was called with, so every downstream handler of
		// this same failure — the page that redirects to /standard-error, ErrorBoundary,
		// the global listeners — reuses this correlationId (= the request's X-Request-ID)
		// instead of minting and logging a second one for the same root cause.
		const logFinalErrorOnce = (error_: unknown): ErrorLogPayload | undefined => {
			if (!shouldLog) return undefined;
			// Skip noisy pseudo-errors we intentionally throw for navigation. Those
			// sentinels already carry the payload logged for the real error (attached
			// where they're thrown), so there's nothing new to log here.
			if (error_ instanceof Error && error_.message.startsWith('Redirecting')) return undefined;
			// Skip cancelled requests (e.g. debounced search / unmount aborts) — these
			// are expected control flow, not failures, and would otherwise flood the
			// logs with entries that have no actionable root cause.
			if (axios.isCancel(error_) || (error_ as AxiosError)?.code === 'ERR_CANCELED') return undefined;
			const statusCode =
				(error_ as AxiosError)?.response?.status ??
				error.response?.status;
			if (statusCode === 401) return undefined;

			const classification = classifyAxiosError(error);
			const message = classification.category === 'network' ? describeNetworkError(error) : error.message;
			const retryAttempt = originalRequest?._retryCount ?? 0;
			const maxRetries = originalRequest?.maxRetries ?? apiMaxRetryCount;
			const apiDetails = buildApiErrorLogDetails(error, classification, retryAttempt, maxRetries, true);

			// The same endpoint + method failing again with the same status (e.g. on a
			// page refresh) reuses the same correlationId instead of minting a new one —
			// a different status for that same endpoint is a genuinely different failure
			// and still gets its own.
			const dedupKey = `api:${apiDetails.apiUrl}:${apiDetails.apiMethod}:${apiDetails.apiResponse?.status ?? 'network'}`;
			// Every request already carries its own X-Request-ID (requestInterceptor).
			// Reuse that as the correlationId on a fresh build instead of minting a
			// second, unrelated UUID — one id now identifies this failure everywhere:
			// the request itself, the /logs entry, the Ref. shown, and the email. Only
			// applies on a genuine cache miss; a reused (refresh) payload keeps its
			// already-assigned correlationId untouched.
			const requestId = originalRequest?.headers?.['X-Request-ID'] as string | undefined;
			const payload: ErrorLogPayload = getOrCreateErrorLogPayload(dedupKey, () => ({
				...buildBaseErrorLogPayload('api', classification.severity, message, error.stack),
				...(requestId ? { correlationId: requestId } : {}),
				...(originalRequest?.sourceComponent ? { componentName: originalRequest.sourceComponent } : {}),
				api: apiDetails,
			}));

			attachErrorLogPayload(error, payload);
			if (error_ !== error) {
				attachErrorLogPayload(error_, payload);
			}
			void logError(payload);
			return payload;
		};

		try {
		if (error?.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			try {
				const newToken = await refreshAccessTokenSafely();
				processQueue(null, newToken);

				try {
					const retryResponse = await instance(originalRequest);
					logger.log('Retry request status:', retryResponse.status);
					return retryResponse;
				} catch (retryError: unknown) {
					const status = (retryError as AxiosError)?.response?.status;
					logger.log('Retry request failed with status:', status);

					if (status === 401) {
						await handleUnauthorizedForSessionExpiry(useKeycloak);
					}

					throw retryError instanceof Error
						? retryError
						: new Error(String(retryError));
				}
			} catch (refreshError: unknown) {
				const refreshStatus = (refreshError as AxiosError)?.response?.status;
				logger.log('Refresh token request failed with status:', refreshStatus);

				if (refreshStatus === 401) {
					await handleUnauthorizedForSessionExpiry(useKeycloak);
				}

				processQueue(refreshError, null);

				throw refreshError instanceof Error
					? refreshError
					: new Error(String(refreshError));
			}
		}


		if (error?.response?.status !== 401) {
			const classification = classifyAxiosError(error);
			const method = (originalRequest?.method ?? 'get').toLowerCase();
			const maxRetries = originalRequest?.maxRetries ?? apiMaxRetryCount;
			const retryCount = originalRequest?._retryCount ?? 0;

			if (method === 'get' && classification.isRetryable && retryCount < maxRetries) {
				originalRequest._retryCount = retryCount + 1;
				const retryAfterHeader = error.response?.headers?.['retry-after'] as string | undefined;
				const delayMs = getRetryDelayMs(originalRequest._retryCount, apiRetryBaseDelayMs, retryAfterHeader);
				await delay(delayMs);
				// Deliberately not awaited: `instance(originalRequest)` re-enters this same
				// interceptor for the retried request. Awaiting it here would let its eventual
				// rejection be caught by *this* invocation's own `catch` below too, logging the
				// same final error twice (once per retry level it unwinds through). Returning
				// the promise directly lets the rejection propagate straight to the caller, so
				// only the deepest (truly final) attempt ever logs.
				// eslint-disable-next-line @typescript-eslint/return-await
				return instance(originalRequest);
			}
		}

		if (error?.response && error.response.status !== 401) {
			const status = error.response.status;
			const requestUrl = originalRequest?.url ?? '';

			logger.log('Server error:', error.response.status, error.response.statusText);

			if (
				status === 403 &&
				!requestUrl.includes('/storage/view-doc') &&
				!requestUrl.includes('/auth/view-doc')
			) {
				const errorData = error.response.data as { errors?: Array<{ errorType?: string; message?: string }> };
				const accessError = errorData?.errors?.find(error_ => error_.errorType === 'ACCESS');
				if (accessError?.message) {
					// Log the real 403 before throwing the navigation-only sentinel below —
					// logFinalErrorOnce's own "Redirecting" prefix check skips re-logging that
					// sentinel when it reaches the catch block, so this fires exactly once.
					const accessPayload = logFinalErrorOnce(error);
					const navigate = getGlobalNavigate();
					if (navigate) {
						void navigate(ROUTE_PATHS.ACCESS_ERROR, {
							// detail = the API's own message, shown on the page (matches the user repo).
							state: { correlationId: accessPayload?.correlationId, detail: accessError.message },
						});
					}
					throw createRedirectSentinel(ACCESS_DENIED_REDIRECT_ERROR_MESSAGE, accessPayload);
				}
			}

			if (status === 400) {
				const errorData = error.response.data as
					| { errors?: Array<{ field?: string | null; message?: string; errorType?: string; pageTemplate?: string; redirect?: string }> }
					| Array<{ field?: string; message?: string; errorType?: string | null }>
					| { message?: string };

				// The body isn't always JSON — a proxy/Tomcat 400 comes back as an HTML string, and
				// `'errors' in <string>` throws a TypeError that replaces the real 400 in the logs.
				if (
					errorData !== null &&
					typeof errorData === 'object' &&
					!Array.isArray(errorData) &&
					'errors' in errorData &&
					Array.isArray(errorData.errors)
				) {
					const displayError = errorData.errors.find(
						(errorItem) => errorItem.errorType === 'VIRUS_ERROR'
					);
					if (displayError?.message) {
						// Log the real 400/VIRUS_ERROR before throwing the navigation-only sentinel below.
						const virusPayload = logFinalErrorOnce(error);
						const navigate = getGlobalNavigate();
					if (navigate) {
						void navigate(ROUTE_PATHS.STANDARD_ERROR, {
							state: {
								title: 'Error Occurred',
								content: displayError.message,
								isHtmlContent: true,
								correlationId: virusPayload?.correlationId,
							},
						});
					}
					throw createRedirectSentinel('Redirecting to error page', virusPayload);
				}
				}

				// "Bad input" 400 errors are NOT intercepted here.
				// They propagate to the calling component's catch/onError so the
				// backend error message can be displayed inline next to the field.
			}

			// Invalid / expired eURL on commons/decrypt → app 404 (same path prefix as current page)
			const isDecryptParametersRequest =
				requestUrl.includes('commons/decrypt?') && !requestUrl.includes('decrypt-string');
			if (isDecryptParametersRequest && (status === 400 || status === 500)) {
				// Log the real decrypt failure before throwing the navigation-only sentinel below.
				const decryptPayload = logFinalErrorOnce(error);
				rememberNotFoundAttemptedUrl();
				window.location.replace(getNotFoundUrlFromCurrentPath());
				throw createRedirectSentinel('Redirecting to not found page', decryptPayload);
			}

			if (process.env.REACT_APP_API_BASE_URL?.toUpperCase().includes('DEV') && status === 500) {
				const errorMessage =
					'Something went wrong. If the problem persists, please reach out to our support team.';
				showGlobalAlert('danger', errorMessage);
			}
		}
		if (error?.request && !error?.response && isNoInternetOrDisconnectedError(error)) {
			logger.log('Network error or no response received:', error.request);
			// No showGlobalAlert here — NetworkStatusToast (mounted in App.tsx) already
			// announces offline/back-online app-wide via the browser's connectivity events.
			const networkErrorMessage =
				'Unable to connect to the server. Please check your internet connection and try again.';
			(error).response = {
				data: { message: networkErrorMessage },
				status: 0,
				statusText: 'Network Error',
				headers: {},
				config: error.config!,
			} as AxiosResponse;
			throw error;
		}
		throw error instanceof Error
			? error
			: new Error(typeof error === 'string' ? error : 'Unknown error');
		} catch (finalError: unknown) {
			// Single logging point (after refresh/retry logic determines the final outcome)
			logFinalErrorOnce(finalError);
			throw finalError instanceof Error ? finalError : new Error(String(finalError));
		}
	};
};
axiosInstance.interceptors.request.use(requestInterceptor);
axiosInstance.interceptors.response.use(
	<T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
	createResponseInterceptor(axiosInstance)
);

// ✅ Wrap axios instance with queue - Fixed version
const queuedAxiosInstance = new Proxy(axiosInstance, {
	get(target, prop) {
		const value: unknown = Reflect.get(target, prop);

		if (typeof value === 'function' && ['get', 'post', 'put', 'patch', 'delete'].includes(String(prop))) {
			return (...args: Array<unknown>) => {
				return requestQueue.enqueue(async () => {
					return (value as (...args: Array<unknown>) => Promise<unknown>).apply(target, args);
				});
			};
		}

		return value;
	},
});

export { queuedAxiosInstance as HttpClient, requestQueue };

const v2AxiosInstance: AxiosInstance = axios.create({
	baseURL: v2ApiURL,
	withCredentials: true,
});

v2AxiosInstance.interceptors.request.use(requestInterceptor);
v2AxiosInstance.interceptors.response.use(
	<T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
	createResponseInterceptor(v2AxiosInstance)
);

const queuedV2AxiosInstance = new Proxy(v2AxiosInstance, {
	get(target, prop) {
		const value: unknown = Reflect.get(target, prop);

		if (typeof value === 'function' && ['get', 'post', 'put', 'patch', 'delete'].includes(String(prop))) {
			return (...args: Array<unknown>) => {
				return requestQueue.enqueue(async () => {
					return (value as (...args: Array<unknown>) => Promise<unknown>).apply(target, args);
				});
			};
		}

		return value;
	},
});

export { queuedV2AxiosInstance as V2ApiClient };

// Make HttpClient available globally for CKEditor config.js
if (typeof window !== 'undefined') {
	(window as Window & { HttpClient?: AxiosInstance }).HttpClient = queuedAxiosInstance;
}

const fileExportAxiosInstance: AxiosInstance = axios.create({
	baseURL: fileExportURL,
	withCredentials: true,
});

fileExportAxiosInstance.interceptors.request.use(requestInterceptor);
fileExportAxiosInstance.interceptors.response.use(
	<T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
	createResponseInterceptor(fileExportAxiosInstance)
);
const queuedFileExportAxiosInstance = new Proxy(fileExportAxiosInstance, {
	get(target, prop) {
		const value: unknown = Reflect.get(target, prop);

		if (typeof value === 'function' && ['get', 'post', 'put', 'patch', 'delete'].includes(String(prop))) {
			return (...args: Array<unknown>) => {
				return requestQueue.enqueue(async () => {
					return (value as (...args: Array<unknown>) => Promise<unknown>).apply(target, args);
				});
			};
		}

		return value;
	},
});

export { queuedFileExportAxiosInstance as FileExportClient };

const htmlToExcelAxiosInstance: AxiosInstance = axios.create({
	baseURL: htmlToExcelURL,
	withCredentials: true,
});

htmlToExcelAxiosInstance.interceptors.request.use(requestInterceptor);
htmlToExcelAxiosInstance.interceptors.response.use(
	<T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
	createResponseInterceptor(htmlToExcelAxiosInstance)
);
const queuedHtmlToExcelAxiosInstance = new Proxy(htmlToExcelAxiosInstance, {
	get(target, prop) {
		const value: unknown = Reflect.get(target, prop);

		if (typeof value === 'function' && ['get', 'post', 'put', 'patch', 'delete'].includes(String(prop))) {
			return (...args: Array<unknown>) => {
				return requestQueue.enqueue(async () => {
					return (value as (...args: Array<unknown>) => Promise<unknown>).apply(target, args);
				});
			};
		}

		return value;
	},
});

export { queuedHtmlToExcelAxiosInstance as HtmlToExcelClient };



const errorLogsAxiosInstance: AxiosInstance = axios.create({
	baseURL: errorLogsAPIBaseurl,
	withCredentials: true,
});

errorLogsAxiosInstance.interceptors.request.use(requestInterceptor);
errorLogsAxiosInstance.interceptors.response.use(
	<T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
	createResponseInterceptor(errorLogsAxiosInstance)
);
const queuedErrorLogsAxiosInstance = new Proxy(errorLogsAxiosInstance, {
	get(target, prop) {
		const value: unknown = Reflect.get(target, prop);

		if (typeof value === 'function' && ['get', 'post', 'put', 'patch', 'delete'].includes(String(prop))) {
			return (...args: Array<unknown>) => {
				return requestQueue.enqueue(async () => {
					return (value as (...args: Array<unknown>) => Promise<unknown>).apply(target, args);
				});
			};
		}

		return value;
	},
});

export { queuedErrorLogsAxiosInstance as ErrorLogsClient };

const startNowProxyAxiosInstance: AxiosInstance = axios.create({
	baseURL: 'https://api.dev1.aoslogin.net/start-now-proxy',
	withCredentials: true,
});

startNowProxyAxiosInstance.interceptors.request.use(requestInterceptor);
startNowProxyAxiosInstance.interceptors.response.use(
	<T>(response: AxiosResponse<T>): AxiosResponse<T> => response,
	createResponseInterceptor(startNowProxyAxiosInstance)
);

const queuedStartNowProxyAxiosInstance = new Proxy(startNowProxyAxiosInstance, {
	get(target, prop) {
		const value: unknown = Reflect.get(target, prop);

		if (typeof value === 'function' && ['get', 'post', 'put', 'patch', 'delete'].includes(String(prop))) {
			return (...args: Array<unknown>) => {
				return requestQueue.enqueue(async () => {
					return (value as (...args: Array<unknown>) => Promise<unknown>).apply(target, args);
				});
			};
		}

		return value;
	},
});

export { queuedStartNowProxyAxiosInstance as StartNowProxyClient };

